// Admin Panel - Create Task Page with Products-First Flow
// File: admin-panel/src/pages/CreateTask.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, onSnapshot, addDoc, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../services/firebase';
import {
  TruckIcon,
  MapPinIcon,
  UserIcon,
  CubeIcon,
  BanknotesIcon,
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { createSupplierOrderNotification } from '../../services/supplierNotificationService';

const generateInvoiceNumber = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `INV-${year}${month}${day}-${random}`;
};

// ==================== ADD SUPPLIER INLINE FORM ====================
// ==================== ADD SUPPLIER INLINE FORM ====================
function AddSupplierInlineForm({ onSave, onCancel, vendorId }) {
  const [loading, setLoading] = useState(false);
  const [supplierData, setSupplierData] = useState({
    name: '',
    phone: '',
    email: '',  // ADD EMAIL FIELD
    address: '',
    landmark: '',
    latitude: '',
    longitude: '',
    gstNumber: '',
    type: 'supplier'
  });

  const handleSave = async () => {
    if (!supplierData.name.trim()) {
      alert('Supplier name is required');
      return;
    }

    // Email validation
    if (supplierData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const supplierDoc = {
        ...supplierData,
        latitude: supplierData.latitude ? parseFloat(supplierData.latitude) : null,
        longitude: supplierData.longitude ? parseFloat(supplierData.longitude) : null,
        vendorId: vendorId,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      const docRef = await addDoc(collection(db, 'customers'), supplierDoc);
      supplierDoc.id = docRef.id;
      onSave(supplierDoc);
    } catch (error) {
      console.error('Error saving supplier:', error);
      alert('Failed to save supplier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-green-800 font-semibold flex items-center gap-2">
          <PlusIcon className="w-4 h-4" />
          Add New Supplier
        </h4>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <input
            type="text"
            value={supplierData.name}
            onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
            placeholder="Supplier Name *"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <input
            type="tel"
            value={supplierData.phone}
            onChange={(e) => setSupplierData({ ...supplierData, phone: e.target.value })}
            placeholder="Phone Number"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
      </div>

      {/* EMAIL FIELD - NEW */}
      <div>
        <input
          type="email"
          value={supplierData.email}
          onChange={(e) => setSupplierData({ ...supplierData, email: e.target.value })}
          placeholder="Email Address (for order notifications)"
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Supplier will receive order notifications if they have an account with this email</p>
      </div>
      
      <div>
        <input
          type="text"
          value={supplierData.address}
          onChange={(e) => setSupplierData({ ...supplierData, address: e.target.value })}
          placeholder="Address"
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <input
            type="text"
            value={supplierData.landmark}
            onChange={(e) => setSupplierData({ ...supplierData, landmark: e.target.value })}
            placeholder="Landmark"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <input
            type="text"
            value={supplierData.latitude}
            onChange={(e) => setSupplierData({ ...supplierData, latitude: e.target.value })}
            placeholder="Latitude"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <input
            type="text"
            value={supplierData.longitude}
            onChange={(e) => setSupplierData({ ...supplierData, longitude: e.target.value })}
            placeholder="Longitude"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
      </div>
      
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={loading || !supplierData.name.trim()}
          className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
        >
          {loading ? 'Saving...' : 'Add Supplier'}
        </button>
      </div>
    </div>
  );
}

// ==================== PRODUCT SELECTION MODAL WITH SUPPLIER ====================
function ProductSelectionModal({ isOpen, onClose, onSelect, selectedProducts, vendorId, suppliers, onSupplierAdded, mode = 'add' }) {
  // mode: 'add' = adding new products (hide already selected), 'edit' = editing all products
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [tempSelected, setTempSelected] = useState([]);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(null); // productId or null

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit') {
        // Edit mode: show all selected products
        setTempSelected([...selectedProducts]);
      } else {
        // Add mode: start fresh for new selections only
        setTempSelected([]);
      }
      setShowAddSupplier(false);
      setSupplierSearchQuery('');
      setShowSupplierDropdown(null);
      setSearchQuery(''); // Reset search query when modal opens
    }
  }, [isOpen, selectedProducts, mode]);

  useEffect(() => {
    if (!isOpen || !vendorId) {
      return;
    }
    
    // Reset loading state when modal opens
    setLoading(true);
    
    const productsQuery = query(
      collection(db, 'products'),
      where('vendorId', '==', vendorId),
      where('isActive', '==', true)
    );
    
    const unsubscribe = onSnapshot(productsQuery, (snapshot) => {
      const productsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(productsList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading products:', error);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [isOpen, vendorId]);

  const filteredProducts = products.filter(p => {
    const name = p.name?.toLowerCase() || '';
    const partNumber = p.partNumber?.toLowerCase() || '';
    const sku = p.sku?.toLowerCase() || '';
    const q = searchQuery.toLowerCase();
    return name.includes(q) || partNumber.includes(q) || sku.includes(q);
  });

  const filteredSuppliers = suppliers.filter(s => {
    const name = s.name?.toLowerCase() || '';
    const phone = s.phone?.toLowerCase() || '';
    const q = supplierSearchQuery.toLowerCase();
    return name.includes(q) || phone.includes(q);
  });

  const handleToggleProduct = (product) => {
    setTempSelected(prev => {
      // In add mode, always add as new (can select same product multiple times for different suppliers)
      // In edit mode, toggle based on temp key
      const tempKey = `temp_${product.id}_${Date.now()}`;
      
      return [...prev, {
        id: product.id,
        tempKey: tempKey, // Temporary key for this modal session
        name: product.name,
        partNumber: product.partNumber || product.sku,
        price: product.price || 0,
        quantity: 1,
        imageUrl: product.imageUrls?.[0] || product.imageUrl,
        stock: product.stock || 0,
        supplierId: null,
        supplierName: '',
        supplierPhone: '',
        supplierAddress: '',
        supplierLandmark: '',
        supplierLatitude: '',
        supplierLongitude: ''
      }];
    });
  };

  // Remove product from temp selection by tempKey
  const handleRemoveFromTemp = (tempKey) => {
    setTempSelected(prev => prev.filter(p => p.tempKey !== tempKey));
  };

  const handleQuantityChange = (tempKey, quantity) => {
    setTempSelected(prev =>
      prev.map(p => p.tempKey === tempKey ? { ...p, quantity: Math.max(1, quantity) } : p)
    );
  };

const handleSupplierSelect = (tempKey, supplier) => {
  setTempSelected(prev =>
    prev.map(p => p.tempKey === tempKey ? { 
      ...p, 
      supplierId: supplier.id,
      supplierName: supplier.name,
      supplierPhone: supplier.phone || '',
      supplierEmail: supplier.email || '',  // ADD THIS
      supplierAddress: supplier.address || '',
      supplierLandmark: supplier.landmark || '',
      supplierLatitude: supplier.latitude?.toString() || '',
      supplierLongitude: supplier.longitude?.toString() || ''
    } : p)
  );
  setShowSupplierDropdown(null);
  setSupplierSearchQuery('');
};

  const handleNewSupplierAdded = (supplier) => {
    onSupplierAdded(supplier);
    setShowAddSupplier(false);
    // If there's an active product waiting for supplier, assign it
    if (showSupplierDropdown) {
      handleSupplierSelect(showSupplierDropdown, supplier);
    }
  };

  const handleConfirm = () => {
    // Check if all selected products have suppliers
    const productsWithoutSupplier = tempSelected.filter(p => !p.supplierId);
    if (productsWithoutSupplier.length > 0) {
      alert(`Please assign suppliers to all products. ${productsWithoutSupplier.length} product(s) need a supplier.`);
      return;
    }
    
    if (mode === 'add') {
      // Add mode: merge new products with existing ones
      // Check for duplicates (same product + same supplier)
      const mergedProducts = [...selectedProducts];
      
      tempSelected.forEach(newProduct => {
        const existingIndex = mergedProducts.findIndex(
          p => p.id === newProduct.id && p.supplierId === newProduct.supplierId
        );
        
        if (existingIndex >= 0) {
          // Same product from same supplier - add quantities
          mergedProducts[existingIndex] = {
            ...mergedProducts[existingIndex],
            quantity: mergedProducts[existingIndex].quantity + newProduct.quantity
          };
        } else {
          // New product or same product from different supplier - add as new entry
          mergedProducts.push({
            ...newProduct,
            // Create unique key for this product+supplier combination
            uniqueKey: `${newProduct.id}_${newProduct.supplierId}_${Date.now()}`
          });
        }
      });
      
      onSelect(mergedProducts);
    } else {
      // Edit mode: replace all products
      onSelect(tempSelected);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {mode === 'edit' ? 'Edit Products & Suppliers' : 'Add Products & Assign Suppliers'}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {mode === 'edit' 
                ? `Modify your ${selectedProducts.length} selected product${selectedProducts.length !== 1 ? 's' : ''}`
                : `${products.length} product${products.length !== 1 ? 's' : ''} available • Select products and assign suppliers`
              }
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products by name or part number..."
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
        
        {/* Products List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-red-600 border-t-transparent"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selected Products Section */}
              {tempSelected.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <h4 className="text-green-800 font-semibold mb-3 flex items-center gap-2">
                    <CheckIcon className="w-5 h-5" />
                    Selected Products ({tempSelected.length})
                  </h4>
                  <div className="space-y-3">
                    {tempSelected.map((selectedItem) => {
                      const assignedSupplier = selectedItem.supplierId 
                        ? suppliers.find(s => s.id === selectedItem.supplierId) 
                        : null;
                      
                      return (
                        <div 
                          key={selectedItem.tempKey} 
                          className="bg-white rounded-lg p-3 border border-green-300"
                        >
                          {/* Product Info Row */}
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {selectedItem.imageUrl ? (
                                <img 
                                  src={selectedItem.imageUrl} 
                                  alt={selectedItem.name} 
                                  className="w-full h-full object-cover rounded-lg" 
                                />
                              ) : (
                                <CubeIcon className="w-6 h-6 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-gray-900 font-medium text-sm truncate">{selectedItem.name}</h5>
                              <p className="text-gray-500 text-xs">{selectedItem.partNumber || 'No Part #'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleQuantityChange(selectedItem.tempKey, selectedItem.quantity - 1)}
                                className="w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded font-bold text-gray-700 text-sm"
                              >
                                -
                              </button>
                              <span className="w-8 text-center text-sm font-medium">{selectedItem.quantity}</span>
                              <button
                                type="button"
                                onClick={() => handleQuantityChange(selectedItem.tempKey, selectedItem.quantity + 1)}
                                className="w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded font-bold text-gray-700 text-sm"
                              >
                                +
                              </button>
                            </div>
                            <span className="text-red-600 font-bold text-sm min-w-[70px] text-right">
                              ₹{(selectedItem.price * selectedItem.quantity).toLocaleString('en-IN')}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFromTemp(selectedItem.tempKey)}
                              className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          </div>
                          
                          {/* Supplier Selection */}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                                <BuildingStorefrontIcon className="w-4 h-4 text-green-600" />
                                Pickup Supplier *
                              </span>
                            </div>
                            
                            {assignedSupplier ? (
                              <div 
                                className="flex items-center justify-between bg-green-100 rounded-lg p-2 cursor-pointer hover:bg-green-200"
                                onClick={() => setShowSupplierDropdown(selectedItem.tempKey)}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                                    <BuildingStorefrontIcon className="w-4 h-4 text-white" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900 text-sm">{assignedSupplier.name}</p>
                                    <p className="text-xs text-gray-500">{assignedSupplier.phone || assignedSupplier.address?.substring(0, 25) || ''}</p>
                                  </div>
                                </div>
                                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                              </div>
                            ) : (
                              <div 
                                className="flex items-center justify-between bg-yellow-100 border border-yellow-300 rounded-lg p-2 cursor-pointer hover:bg-yellow-200"
                                onClick={() => setShowSupplierDropdown(selectedItem.tempKey)}
                              >
                                <span className="text-yellow-700 text-sm">Select supplier for pickup</span>
                                <ChevronDownIcon className="w-4 h-4 text-yellow-600" />
                              </div>
                            )}
                            
                            {/* Supplier Dropdown */}
                            {showSupplierDropdown === selectedItem.tempKey && (
                              <div className="mt-2 space-y-2">
                                <div className="relative">
                                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                  <input
                                    type="text"
                                    value={supplierSearchQuery}
                                    onChange={(e) => setSupplierSearchQuery(e.target.value)}
                                    placeholder="Search suppliers..."
                                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                    autoFocus
                                  />
                                </div>
                                
                                {showAddSupplier ? (
                                  <AddSupplierInlineForm
                                    onSave={handleNewSupplierAdded}
                                    onCancel={() => setShowAddSupplier(false)}
                                    vendorId={vendorId}
                                  />
                                ) : (
                                  <>
                                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                                      {filteredSuppliers.length > 0 ? (
                                        filteredSuppliers.map(supplier => (
                                          <div
                                            key={supplier.id}
                                            className={`p-2 cursor-pointer hover:bg-green-50 border-b border-gray-100 last:border-0 ${
                                              selectedItem.supplierId === supplier.id ? 'bg-green-100' : ''
                                            }`}
                                            onClick={() => handleSupplierSelect(selectedItem.tempKey, supplier)}
                                          >
                                            <div className="flex items-center gap-2">
                                              <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                                                <BuildingStorefrontIcon className="w-3.5 h-3.5 text-green-600" />
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 text-sm">{supplier.name}</p>
                                                <p className="text-xs text-gray-500 truncate">{supplier.phone || supplier.address?.substring(0, 30) || 'No details'}</p>
                                              </div>
                                              {selectedItem.supplierId === supplier.id && (
                                                <CheckIcon className="w-4 h-4 text-green-600" />
                                              )}
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="p-3 text-center text-gray-500 text-sm">No suppliers found</div>
                                      )}
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <button
                                        type="button"
                                        onClick={() => setShowAddSupplier(true)}
                                        className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                                      >
                                        <PlusIcon className="w-3 h-3" />
                                        Add New Supplier
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setShowSupplierDropdown(null);
                                          setSupplierSearchQuery('');
                                        }}
                                        className="text-xs text-gray-500 hover:text-gray-700"
                                      >
                                        Close
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Product Catalog Section */}
              <div>
                <h4 className="text-gray-700 font-semibold mb-3 flex items-center gap-2">
                  <CubeIcon className="w-5 h-5" />
                  Product Catalog - Click to Add
                </h4>
                {filteredProducts.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {filteredProducts.map(product => (
                      <div
                        key={product.id}
                        onClick={() => handleToggleProduct(product)}
                        className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-xl cursor-pointer transition-all"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {product.imageUrls?.[0] || product.imageUrl ? (
                            <img 
                              src={product.imageUrls?.[0] || product.imageUrl} 
                              alt={product.name} 
                              className="w-full h-full object-cover rounded-lg" 
                            />
                          ) : (
                            <CubeIcon className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-gray-900 font-medium text-sm truncate">{product.name}</h5>
                          <p className="text-gray-500 text-xs">{product.partNumber || product.sku || 'No Part #'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-red-600 font-bold text-sm">₹{product.price?.toLocaleString('en-IN')}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${product.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {product.stock > 0 ? `${product.stock} stock` : 'No stock'}
                            </span>
                          </div>
                        </div>
                        <PlusIcon className="w-6 h-6 text-blue-500" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CubeIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">
                      {searchQuery ? 'No products found matching your search' : 'No products available'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-sm text-gray-600">
                {tempSelected.length} product{tempSelected.length !== 1 ? 's' : ''} selected
              </span>
              {tempSelected.length > 0 && (
                <span className="text-xs text-gray-500 ml-2">
                  ({tempSelected.filter(p => p.supplierId).length} with suppliers)
                </span>
              )}
            </div>
            {tempSelected.length > 0 && (
              <span className="text-sm font-medium text-gray-900">
                Total: ₹{tempSelected.reduce((sum, p) => sum + (p.price * p.quantity), 0).toLocaleString('en-IN')}
              </span>
            )}
          </div>
          
          {/* Warning if products without suppliers */}
          {tempSelected.length > 0 && tempSelected.some(p => !p.supplierId) && (
            <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded-lg">
              <p className="text-yellow-800 text-xs">
                ⚠️ {tempSelected.filter(p => !p.supplierId).length} product(s) need a supplier assigned
              </p>
            </div>
          )}
          
          <button 
            onClick={handleConfirm}
            disabled={tempSelected.length === 0 || tempSelected.some(p => !p.supplierId)}
            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {tempSelected.length === 0 
              ? 'Select Products' 
              : tempSelected.some(p => !p.supplierId)
                ? `Assign Suppliers to Continue`
                : mode === 'edit'
                  ? `Save Changes (${tempSelected.length})`
                  : `Add ${tempSelected.length} Product${tempSelected.length !== 1 ? 's' : ''}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== PICKUP SUMMARY CARD (Read-only view grouped by supplier) ====================
function PickupSummaryCard({ pickup, index, onRemoveProduct, onUpdateProductQty }) {
  const [isExpanded, setIsExpanded] = useState(true);

  const totalAmount = pickup.products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  const totalItems = pickup.products.reduce((sum, p) => sum + p.quantity, 0);

  return (
    <div className="bg-white border-2 border-green-200 rounded-2xl overflow-hidden shadow-sm">
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
              <BuildingStorefrontIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="text-gray-900 font-semibold">{pickup.supplierName}</h4>
              <p className="text-gray-500 text-sm">
                {pickup.products.length} product{pickup.products.length !== 1 ? 's' : ''} • {totalItems} item{totalItems !== 1 ? 's' : ''} • ₹{totalAmount.toLocaleString('en-IN')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Supplier Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">Contact</label>
              <p className="text-gray-900">{pickup.supplierName}</p>
              {pickup.supplierPhone && <p className="text-gray-500 text-sm">{pickup.supplierPhone}</p>}
            </div>
            {pickup.supplierAddress && (
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">Pickup Address</label>
                <p className="text-gray-600 text-sm">{pickup.supplierAddress}</p>
              </div>
            )}
          </div>

          {/* Products */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h5 className="text-gray-900 font-medium mb-3">Products</h5>
            <div className="space-y-2">
              {pickup.products.map((product, pIdx) => (
                <div key={product.id} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <CubeIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-medium text-sm truncate">{product.name}</p>
                    <p className="text-gray-500 text-xs">₹{product.price?.toLocaleString('en-IN')} × {product.quantity}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onUpdateProductQty(index, pIdx, product.quantity - 1)}
                      className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 text-sm font-bold"
                    >
                      -
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{product.quantity}</span>
                    <button
                      type="button"
                      onClick={() => onUpdateProductQty(index, pIdx, product.quantity + 1)}
                      className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 text-sm font-bold"
                    >
                      +
                    </button>
                  </div>
                  <span className="text-red-600 font-bold text-sm min-w-[60px] text-right">
                    ₹{(product.price * product.quantity).toLocaleString('en-IN')}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemoveProduct(index, pIdx)}
                    className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {/* Pickup Total */}
              <div className="flex justify-end pt-2 border-t border-gray-200 mt-2">
                <div className="text-right">
                  <span className="text-gray-500 text-sm">Pickup Total: </span>
                  <span className="text-lg font-bold text-green-600">₹{totalAmount.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
function CreateTask() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [porters, setPorters] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  
  // Suppliers for pickup
  const [suppliers, setSuppliers] = useState([]);
  
  // Product Modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [productModalMode, setProductModalMode] = useState('add'); // 'add' or 'edit'

  // All selected products (with supplier info)
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Form Data
  const [formData, setFormData] = useState({
    invoiceNumber: generateInvoiceNumber(),
    orderId: '',
    
    // Delivery Details (Customer)
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    deliveryAddress: '',
    deliveryLandmark: '',
    deliveryLatitude: '',
    deliveryLongitude: '',
    
    // Other Details
    packageDescription: '',
    isFragile: false,
    specialInstructions: '',
    paymentMode: 'cash',
    paymentAmount: '',
    porterId: '',
    priority: 'normal',
    estimatedTime: '45 mins',
    distance: '',
  });

  // Get current authenticated admin user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        
        try {
          const vendorDoc = await getDocs(query(
            collection(db, 'vendors'),
            where('uid', '==', user.uid)
          ));
          
          if (!vendorDoc.empty) {
            const profile = { id: vendorDoc.docs[0].id, ...vendorDoc.docs[0].data() };
            setVendorProfile(profile);
          }
        } catch (error) {
          console.error('Error fetching vendor profile:', error);
        }
      } else {
        setCurrentUser(null);
        setVendorProfile(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load suppliers
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    const suppliersQuery = query(
      collection(db, 'customers'),
      where('vendorId', '==', currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(suppliersQuery, (snapshot) => {
      const suppliersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSuppliers(suppliersList);
    });
    
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Load porters
  useEffect(() => {
    const portersQuery = query(collection(db, 'porters'), where('isActive', '==', true));
    const unsubscribe = onSnapshot(portersQuery, (snapshot) => {
      const portersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPorters(portersList);
    });
    return () => unsubscribe();
  }, []);

  // Calculate payment amount from selected products
  useEffect(() => {
    const total = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    if (total > 0) {
      setFormData(prev => ({ ...prev, paymentAmount: total.toString() }));
    }
  }, [selectedProducts]);

  // Group products by supplier for display
// Group products by supplier for display
const groupedPickups = React.useMemo(() => {
  const groups = {};
  selectedProducts.forEach(product => {
    if (!product.supplierId) return;
    if (!groups[product.supplierId]) {
      groups[product.supplierId] = {
        supplierId: product.supplierId,
        supplierName: product.supplierName,
        supplierPhone: product.supplierPhone,
        supplierEmail: product.supplierEmail || '',  // ADD THIS
        supplierAddress: product.supplierAddress,
        supplierLandmark: product.supplierLandmark || '',
        supplierLatitude: product.supplierLatitude || '',
        supplierLongitude: product.supplierLongitude || '',
        products: []
      };
    }
    groups[product.supplierId].products.push(product);
  });
  return Object.values(groups);
}, [selectedProducts]);

  // Handle products selected from modal
  const handleProductsSelected = (products) => {
    setSelectedProducts(products);
  };

  // Handle supplier added
  const handleSupplierAdded = (supplier) => {
    // Supplier will be auto-added via the Firestore subscription
  };

  // Remove product from a pickup
  const removeProductFromPickup = (pickupIndex, productIndex) => {
    const pickup = groupedPickups[pickupIndex];
    const productToRemove = pickup.products[productIndex];
    
    // Use uniqueKey if available, otherwise match by id + supplierId
    setSelectedProducts(prev => prev.filter(p => {
      if (productToRemove.uniqueKey && p.uniqueKey) {
        return p.uniqueKey !== productToRemove.uniqueKey;
      }
      // Match by both product id AND supplier id
      return !(p.id === productToRemove.id && p.supplierId === productToRemove.supplierId);
    }));
  };

  // Update product quantity
  const updateProductQuantity = (pickupIndex, productIndex, quantity) => {
    if (quantity < 1) {
      // Remove product if quantity is 0
      removeProductFromPickup(pickupIndex, productIndex);
      return;
    }
    const pickup = groupedPickups[pickupIndex];
    const productToUpdate = pickup.products[productIndex];
    
    // Use uniqueKey if available, otherwise match by id + supplierId
    setSelectedProducts(prev => 
      prev.map(p => {
        if (productToUpdate.uniqueKey && p.uniqueKey) {
          return p.uniqueKey === productToUpdate.uniqueKey ? { ...p, quantity } : p;
        }
        // Match by both product id AND supplier id
        if (p.id === productToUpdate.id && p.supplierId === productToUpdate.supplierId) {
          return { ...p, quantity };
        }
        return p;
      })
    );
  };

  // Calculate totals
  const totalProducts = selectedProducts.length;
  const totalItems = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
  const totalAmount = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);

  // Submit
// Update handleSubmit function:
const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    if (!currentUser) {
      alert('You must be logged in to create tasks');
      setLoading(false);
      return;
    }

    if (!formData.customerName || !formData.customerPhone) {
      alert('Please fill in customer details');
      setLoading(false);
      return;
    }

    if (!formData.deliveryAddress) {
      alert('Please fill in delivery address');
      setLoading(false);
      return;
    }

    if (groupedPickups.length === 0) {
      alert('Please add products with suppliers');
      setLoading(false);
      return;
    }

    const selectedPorter = porters.find(p => p.id === formData.porterId);

    // 1. Create Invoice First
    const invoiceData = {
      invoiceNumber: formData.invoiceNumber,
      customerName: formData.customerName.trim(),
      customerEmail: formData.customerEmail?.trim() || '',
      customerPhone: formData.customerPhone.trim(),
      customerAddress: formData.deliveryAddress.trim(),
      customerGST: '',
      saleMode: 'online',
      items: selectedProducts.map(p => ({
        productId: p.id,
        name: p.name,
        quantity: p.quantity,
        rate: p.price,
        amount: p.price * p.quantity,
        isManual: false,
        hsnCode: '',
        gstRate: 0
      })),
      subtotal: totalAmount,
      discount: 0,
      invoiceType: 'without_gst',
      totalTaxAmount: 0,
      totalAmount: totalAmount,
      createdAt: Timestamp.fromDate(new Date()),
      notes: `Online delivery order - ${groupedPickups.length} pickup(s)`,
      terms: '',
      status: 'pending',
      paymentMethod: formData.paymentMode,
      vendorId: currentUser.uid,
    };

    const invoiceRef = await addDoc(collection(db, 'invoices'), invoiceData);

    // 2. Create Task
    const taskData = {
      vendorId: currentUser.uid,
      createdByMainAdmin: true,
      saleMode: 'online',
      
      invoiceNumber: formData.invoiceNumber,
      invoiceId: invoiceRef.id,
      orderId: formData.orderId || null,
      status: formData.porterId ? 'pending' : 'unassigned',
      
      customer: {
        name: formData.customerName,
        phone: formData.customerPhone,
        email: formData.customerEmail || null,
      },
      
      pickups: groupedPickups.map(pickup => ({
        address: pickup.supplierAddress || '',
        landmark: pickup.supplierLandmark || null,
        contactName: pickup.supplierName,
        contactPhone: pickup.supplierPhone || null,
        contactEmail: pickup.supplierEmail || null,  // ADD EMAIL
        supplierId: pickup.supplierId,
        latitude: pickup.supplierLatitude ? parseFloat(pickup.supplierLatitude) : null,
        longitude: pickup.supplierLongitude ? parseFloat(pickup.supplierLongitude) : null,
        products: pickup.products.map(prod => ({
          id: prod.id,
          name: prod.name,
          partNumber: prod.partNumber,
          price: prod.price,
          quantity: prod.quantity,
          imageUrl: prod.imageUrl || null,
        })),
        productCount: pickup.products.length,
        itemCount: pickup.products.reduce((sum, prod) => sum + prod.quantity, 0),
        amount: pickup.products.reduce((sum, prod) => sum + (prod.price * prod.quantity), 0),
      })),
      
      delivery: {
        address: formData.deliveryAddress,
        landmark: formData.deliveryLandmark || null,
        latitude: formData.deliveryLatitude ? parseFloat(formData.deliveryLatitude) : null,
        longitude: formData.deliveryLongitude ? parseFloat(formData.deliveryLongitude) : null,
      },
      
      package: {
        products: selectedProducts.map(p => ({
          id: p.id,
          name: p.name,
          partNumber: p.partNumber,
          price: p.price,
          quantity: p.quantity,
          imageUrl: p.imageUrl || null,
        })),
        description: formData.packageDescription || selectedProducts.map(p => p.name).join(', '),
        isFragile: formData.isFragile,
        specialInstructions: formData.specialInstructions || null,
        itemCount: totalItems,
        productCount: totalProducts,
      },
      
      payment: {
        mode: formData.paymentMode,
        amount: parseFloat(formData.paymentAmount) || totalAmount,
        collected: formData.paymentMode === 'prepaid',
        deposited: false,
        status: formData.paymentMode === 'prepaid' ? 'verified' : 'pending',
      },
      
      porterId: formData.porterId || null,
      porterName: selectedPorter?.name || null,
      priority: formData.priority,
      estimatedTime: formData.estimatedTime,
      distance: formData.distance || null,
      pickupCount: groupedPickups.length,
      
      timeline: [{ 
        status: 'created', 
        timestamp: new Date().toISOString(), 
        notes: 'Task created' 
      }],
      
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const taskRef = await addDoc(collection(db, 'tasks'), taskData);

    // 3. Update invoice with task reference
    await updateDoc(invoiceRef, { taskId: taskRef.id });

    // 4. NOTIFY EACH SUPPLIER - Creates orders in their account
    const taskWithIds = {
      ...taskData,
      taskId: taskRef.id,
      invoiceId: invoiceRef.id
    };

    for (const pickup of groupedPickups) {
      if (pickup.supplierEmail) {
        try {
          await createSupplierOrderNotification(taskWithIds, {
            ...pickup,
            supplierEmail: pickup.supplierEmail || pickup.products[0]?.supplierEmail
          }, vendorProfile);
          console.log(`✅ Notified supplier: ${pickup.supplierName}`);
        } catch (notifyError) {
          console.error(`Failed to notify supplier ${pickup.supplierName}:`, notifyError);
        }
      }
    }

    alert('Task created successfully! Suppliers have been notified.');
    navigate('/spares-admin/tasks');
  } catch (error) {
    console.error('Create task error:', error);
    alert('Failed to create task. Please try again.');
  } finally {
    setLoading(false);
  }
};

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
          <p className="text-gray-500 font-medium">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Create Delivery Task</h1>
        <p className="text-gray-500 mt-1">Select products and assign suppliers for pickup</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice & Order */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TruckIcon className="w-5 h-5 text-red-600" />
            Order Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Invoice Number</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Order ID (Optional)</label>
              <input
                type="text"
                value={formData.orderId}
                onChange={(e) => setFormData({ ...formData, orderId: e.target.value })}
                placeholder="e.g., ORD-12345"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>

        {/* Products & Suppliers Selection */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CubeIcon className="w-5 h-5 text-green-600" />
              Products & Pickup Locations
            </h3>
            <div className="flex items-center gap-2">
              {selectedProducts.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setProductModalMode('edit');
                    setShowProductModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Products
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setProductModalMode('add');
                  setShowProductModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl transition-colors shadow-lg shadow-green-600/20"
              >
                <PlusIcon className="w-4 h-4" />
                Add Products
              </button>
            </div>
          </div>

          {/* Grouped Pickups Display */}
          {groupedPickups.length > 0 ? (
            <div className="space-y-4">
              {groupedPickups.map((pickup, index) => (
                <PickupSummaryCard
                  key={pickup.supplierId}
                  pickup={pickup}
                  index={index}
                  onRemoveProduct={removeProductFromPickup}
                  onUpdateProductQty={updateProductQuantity}
                />
              ))}

              {/* Summary */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-gray-500 text-sm">Pickups</p>
                      <p className="text-2xl font-bold text-gray-900">{groupedPickups.length}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500 text-sm">Products</p>
                      <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500 text-sm">Items</p>
                      <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500 text-sm">Total Amount</p>
                    <p className="text-3xl font-bold text-red-600">₹{totalAmount.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
              <CubeIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 mb-3">No products selected</p>
              <button
                type="button"
                onClick={() => {
                  setProductModalMode('add');
                  setShowProductModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add Products & Assign Suppliers
              </button>
            </div>
          )}
        </div>

        {/* Delivery Location (Customer) */}
        <div className="bg-white border-2 border-red-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPinIcon className="w-5 h-5 text-red-600" />
            Delivery Location (Customer)
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Customer Name *</label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                placeholder="Full name"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Phone Number *</label>
              <input
                type="tel"
                value={formData.customerPhone}
                onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                placeholder="+91 98765 43210"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 text-sm font-medium mb-2">Email (Optional)</label>
              <input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                placeholder="customer@example.com"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">Delivery Address *</label>
            <textarea
              value={formData.deliveryAddress}
              onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
              placeholder="Full delivery address"
              rows={2}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Landmark</label>
              <input
                type="text"
                value={formData.deliveryLandmark}
                onChange={(e) => setFormData({ ...formData, deliveryLandmark: e.target.value })}
                placeholder="Near..."
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Latitude (Optional)</label>
              <input
                type="text"
                value={formData.deliveryLatitude}
                onChange={(e) => setFormData({ ...formData, deliveryLatitude: e.target.value })}
                placeholder="e.g., 12.9716"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Longitude (Optional)</label>
              <input
                type="text"
                value={formData.deliveryLongitude}
                onChange={(e) => setFormData({ ...formData, deliveryLongitude: e.target.value })}
                placeholder="e.g., 77.5946"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
        </div>

        {/* Package Details */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CubeIcon className="w-5 h-5 text-red-600" />
            Package Details
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Package Description</label>
              <input
                type="text"
                value={formData.packageDescription}
                onChange={(e) => setFormData({ ...formData, packageDescription: e.target.value })}
                placeholder="Auto-filled from products or enter manually"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isFragile"
                checked={formData.isFragile}
                onChange={(e) => setFormData({ ...formData, isFragile: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <label htmlFor="isFragile" className="text-gray-900 font-medium">
                ⚠️ Fragile - Handle with care
              </label>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Special Instructions</label>
              <textarea
                value={formData.specialInstructions}
                onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
                placeholder="Any special handling instructions..."
                rows={2}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BanknotesIcon className="w-5 h-5 text-red-600" />
            Payment Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Payment Mode *</label>
              <div className="flex gap-2">
                {['cash', 'online', 'prepaid'].map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setFormData({ ...formData, paymentMode: mode })}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                      formData.paymentMode === mode
                        ? mode === 'cash' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400'
                        : mode === 'online' ? 'bg-blue-100 text-blue-800 border-2 border-blue-400'
                        : 'bg-green-100 text-green-800 border-2 border-green-400'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                    }`}
                  >
                    {mode === 'cash' && '💵 '}
                    {mode === 'online' && '💳 '}
                    {mode === 'prepaid' && '✅ '}
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Amount (₹) *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
                <input
                  type="number"
                  value={formData.paymentAmount}
                  onChange={(e) => setFormData({ ...formData, paymentAmount: e.target.value })}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Assignment */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-red-600" />
            Assign Porter
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Select Porter</label>
              <select
                value={formData.porterId}
                onChange={(e) => setFormData({ ...formData, porterId: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">-- Unassigned --</option>
                {porters.filter(p => p.isOnline).map(porter => (
                  <option key={porter.id} value={porter.id}>🟢 {porter.name}</option>
                ))}
                {porters.filter(p => !p.isOnline).map(porter => (
                  <option key={porter.id} value={porter.id}>⚫ {porter.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="normal">🔵 Normal</option>
                <option value="high">🟠 High</option>
                <option value="urgent">🔴 Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Estimated Time</label>
              <select
                value={formData.estimatedTime}
                onChange={(e) => setFormData({ ...formData, estimatedTime: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="30 mins">30 mins</option>
                <option value="45 mins">45 mins</option>
                <option value="1 hour">1 hour</option>
                <option value="1.5 hours">1.5 hours</option>
                <option value="2 hours">2 hours</option>
                <option value="2.5 hours">2.5 hours</option>
                <option value="3 hours">3 hours</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/spares-admin/tasks')}
            className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || totalProducts === 0}
            className="flex-1 py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-red-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Creating...
              </span>
            ) : (
              `🚚 Create Task (${groupedPickups.length} Pickup${groupedPickups.length !== 1 ? 's' : ''})`
            )}
          </button>
        </div>
      </form>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        onSelect={handleProductsSelected}
        selectedProducts={selectedProducts}
        vendorId={currentUser?.uid}
        suppliers={suppliers}
        onSupplierAdded={handleSupplierAdded}
        mode={productModalMode}
      />
    </div>
  );
}

export default CreateTask;